import { createContext, useContext, useState, useCallback } from "react";

const STORE_KEY = "ela_lang";

const dict = {
  en: {
    "app.name": "Easy Learning Academy",
    "app.tagline": "Practice languages, the easy way",
    "nav.dashboard": "Dashboard",
    "nav.logout": "Logout",
    "nav.users": "Users",
    "nav.courses": "Courses",
    "nav.progress": "Progress",

    "landing.hero_title": "Learn a language with your academy",
    "landing.hero_sub": "Lessons, tasks and guided practice assigned by your teacher — with instant AI feedback on translation, pronunciation and writing.",
    "landing.cta_login": "Student / Teacher Login",
    "landing.private_note": "Access is private. Students do not self-register — the Academy provides your username and password.",
    "landing.f1_title": "Assigned lessons",
    "landing.f1_text": "See only the lessons, tasks and exercises your teacher assigns to you.",
    "landing.f2_title": "Speak & listen",
    "landing.f2_text": "Practice pronunciation and shadowing with audio and instant correction.",
    "landing.f3_title": "Qualitative progress",
    "landing.f3_text": "Encouraging feedback: Good job, Great effort, Getting better.",
    "landing.bilingual": "Bilingual platform — Spanish & English.",
    "landing.footer": "Private access for enrolled students of Easy Learning Academy.",

    "login.title": "Welcome back",
    "login.subtitle": "Sign in to continue to your dashboard",
    "login.username": "Username",
    "login.password": "Password",
    "login.username_ph": "Enter your username",
    "login.password_ph": "Enter your password",
    "login.signin": "Sign In",
    "login.testcreds": "Test Credentials",
    "login.back_home": "Back to home",

    "common.create": "Create",
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.close": "Close",
    "common.submit": "Submit",
    "common.loading": "Loading...",
    "common.back": "Back",
    "common.none": "None",
    "common.optional": "optional",
    "common.title": "Title",
    "common.description": "Description",
    "common.level": "Level",
    "common.language": "Language",
    "common.role": "Role",
    "common.status": "Status",
    "common.actions": "Actions",
    "common.student": "Student",
    "common.teacher": "Teacher",
    "common.admin": "Admin",
    "common.active": "Active",
    "common.suspended": "Suspended",
    "common.reset": "Reset",
    "common.suspend": "Suspend",
    "common.reactivate": "Reactivate",
    "common.assign": "Assign",
    "common.play": "Play",
    "common.listen": "Listen",
    "common.record": "Record",
    "common.stop": "Stop",
    "common.send": "Send",
    "common.add": "Add",
    "common.name": "Full name",
    "common.username": "Username",
    "common.password": "Password",
    "common.checking": "Checking...",
    "common.try_again": "Try again",

    "admin.users_title": "User Management",
    "admin.users_sub": "Create users, reset passwords, and manage account status.",
    "admin.create_user": "Create User",
    "admin.courses_title": "Courses & Levels",
    "admin.courses_sub": "Create courses, set levels and assign teachers and students.",
    "admin.create_course": "Create Course",
    "admin.manage_content": "Manage content",
    "admin.assign_teacher": "Assign Teacher",
    "admin.assign_student": "Assign Student",
    "admin.progress_title": "Students Progress",
    "admin.progress_sub": "Overview of every student's activity and average score.",
    "admin.avg_score": "Avg. score",
    "admin.submissions": "Submissions",
    "admin.latest": "Latest grade",
    "admin.native": "Native language",

    "teacher.my_courses": "My Courses",
    "teacher.my_courses_sub": "Build lessons, exercises and review student progress.",
    "teacher.new_course": "New Course",
    "teacher.add_lesson": "Add Lesson",
    "teacher.lesson_content": "Lesson text (reading / shadowing)",
    "teacher.media": "Media (video / audio / image)",
    "teacher.add_exercise": "Add Exercise",
    "teacher.exercise_type": "Exercise type",
    "teacher.no_lessons": "No lessons yet.",
    "teacher.manage": "Manage",
    "teacher.review": "Review & comment",
    "teacher.comment": "Teacher comment",
    "teacher.save_feedback": "Save feedback",
    "teacher.no_submissions": "No submissions yet.",

    "student.my_learning": "My Learning",
    "student.my_learning_sub": "Courses assigned to you. Tap a course to start.",
    "student.start": "Start learning",
    "student.practice": "Practice",
    "student.no_courses": "You have no assigned courses yet.",
    "student.progress": "My Progress",
    "student.no_progress": "You haven't practiced any exercises yet.",
    "student.your_answer": "Your answer",
    "student.correct_answer": "Suggested answer",
    "student.heard": "We heard",
    "student.feedback": "Feedback",
    "student.score": "Score",
    "student.submit_answer": "Submit answer",
    "student.translate_here": "Type your translation here",
    "student.write_here": "Write your answer here",
    "student.record_prompt": "Tap record, say the phrase, then stop and submit.",
    "student.select_one": "Select the phrase you hear",
    "student.tabs_lessons": "Lessons",

    "ex.translation": "Translation",
    "ex.pronunciation": "Pronunciation",
    "ex.shadowing": "Shadowing",
    "ex.listen_select": "Listen & Select",
    "ex.image_sentence": "Image → Sentence",
    "ex.reading_repetition": "Reading & Repetition",
    "ex.guided_writing": "Guided Writing",
    "ex.short_quiz": "Short Quiz",

    "grade.good_job": "Good job",
    "grade.great_effort": "Great effort",
    "grade.getting_better": "Getting better",
    "grade.do_better": "You can do better",
    "grade.excellent": "Excellent",
    "grade.try_again": "Try again",

    "ex.sentence_building": "Build Sentence",
    "ex.coming_soon": "Coming soon",

    "student.overview_title": "Your progress",
    "student.complete_lesson": "Complete lesson",
    "student.completed": "Completed",
    "student.lesson_completed": "Lesson completed!",
    "student.locked": "Locked",
    "student.locked_hint": "Complete the previous lesson to unlock this one.",
    "student.available": "Available",
    "student.explanation": "Explanation",
    "student.vocabulary": "Vocabulary",
    "student.examples": "Example phrases",
    "student.back_to_courses": "Back to courses",
    "student.modules": "Modules",
    "student.percent_complete": "complete",
    "student.studied_today": "You studied today!",
    "student.keep_going": "Keep going!",
    "student.avg": "Average",
    "student.lessons_done": "Lessons completed",
    "student.exercises": "exercises",
    "student.voice_disabled": "Voice pronunciation evaluation will be available soon.",
    "student.tap_words": "Tap the words in the correct order",
    "student.clear": "Clear",

    "teacher.add_module": "Add Module",
    "teacher.new_module": "New Module",
    "teacher.module": "Module",
    "teacher.modules": "Modules",
    "teacher.no_modules": "No modules yet. Create one to organize your lessons.",
    "teacher.select_module": "Select a module",
    "teacher.lesson_explanation": "Explanation (grammar / notes)",
    "teacher.status": "Status",
    "teacher.draft": "Draft",
    "teacher.published": "Published",
    "teacher.move_up": "Move up",
    "teacher.move_down": "Move down",
    "teacher.no_module": "No module",
    "teacher.exercises": "exercises",
    "common.pdf": "PDF",
  },
  es: {
    "app.name": "Easy Learning Academy",
    "app.tagline": "Practica idiomas de forma fácil",
    "nav.dashboard": "Panel",
    "nav.logout": "Salir",
    "nav.users": "Usuarios",
    "nav.courses": "Cursos",
    "nav.progress": "Progreso",

    "landing.hero_title": "Aprende un idioma con tu academia",
    "landing.hero_sub": "Lecciones, tareas y práctica guiada asignadas por tu profesor — con retroalimentación instantánea de IA en traducción, pronunciación y escritura.",
    "landing.cta_login": "Acceso Estudiante / Profesor",
    "landing.private_note": "El acceso es privado. Los estudiantes no se registran solos — la Academia entrega tu usuario y contraseña.",
    "landing.f1_title": "Lecciones asignadas",
    "landing.f1_text": "Ve solo las lecciones, tareas y ejercicios que tu profesor te asigna.",
    "landing.f2_title": "Habla y escucha",
    "landing.f2_text": "Practica pronunciación y shadowing con audio y corrección instantánea.",
    "landing.f3_title": "Progreso cualitativo",
    "landing.f3_text": "Retroalimentación motivadora: Buen trabajo, Gran esfuerzo, Vas mejorando.",
    "landing.bilingual": "Plataforma bilingüe — Español e Inglés.",
    "landing.footer": "Acceso privado para estudiantes inscritos de Easy Learning Academy.",

    "login.title": "Bienvenido de nuevo",
    "login.subtitle": "Inicia sesión para entrar a tu panel",
    "login.username": "Usuario",
    "login.password": "Contraseña",
    "login.username_ph": "Escribe tu usuario",
    "login.password_ph": "Escribe tu contraseña",
    "login.signin": "Iniciar sesión",
    "login.testcreds": "Credenciales de prueba",
    "login.back_home": "Volver al inicio",

    "common.create": "Crear",
    "common.cancel": "Cancelar",
    "common.save": "Guardar",
    "common.close": "Cerrar",
    "common.submit": "Enviar",
    "common.loading": "Cargando...",
    "common.back": "Volver",
    "common.none": "Ninguno",
    "common.optional": "opcional",
    "common.title": "Título",
    "common.description": "Descripción",
    "common.level": "Nivel",
    "common.language": "Idioma",
    "common.role": "Rol",
    "common.status": "Estado",
    "common.actions": "Acciones",
    "common.student": "Estudiante",
    "common.teacher": "Profesor",
    "common.admin": "Administrador",
    "common.active": "Activo",
    "common.suspended": "Suspendido",
    "common.reset": "Resetear",
    "common.suspend": "Suspender",
    "common.reactivate": "Reactivar",
    "common.assign": "Asignar",
    "common.play": "Reproducir",
    "common.listen": "Escuchar",
    "common.record": "Grabar",
    "common.stop": "Detener",
    "common.send": "Enviar",
    "common.add": "Agregar",
    "common.name": "Nombre completo",
    "common.username": "Usuario",
    "common.password": "Contraseña",
    "common.checking": "Verificando...",
    "common.try_again": "Intentar de nuevo",

    "admin.users_title": "Gestión de Usuarios",
    "admin.users_sub": "Crea usuarios, resetea contraseñas y gestiona el estado de las cuentas.",
    "admin.create_user": "Crear Usuario",
    "admin.courses_title": "Cursos y Niveles",
    "admin.courses_sub": "Crea cursos, asigna niveles y asigna profesores y estudiantes.",
    "admin.create_course": "Crear Curso",
    "admin.manage_content": "Gestionar contenido",
    "admin.assign_teacher": "Asignar Profesor",
    "admin.assign_student": "Asignar Estudiante",
    "admin.progress_title": "Progreso de Estudiantes",
    "admin.progress_sub": "Resumen de la actividad y el promedio de cada estudiante.",
    "admin.avg_score": "Prom.",
    "admin.submissions": "Entregas",
    "admin.latest": "Última nota",
    "admin.native": "Idioma natal",

    "teacher.my_courses": "Mis Cursos",
    "teacher.my_courses_sub": "Crea lecciones, ejercicios y revisa el progreso de los estudiantes.",
    "teacher.new_course": "Nuevo Curso",
    "teacher.add_lesson": "Agregar Lección",
    "teacher.lesson_content": "Texto de la lección (lectura / shadowing)",
    "teacher.media": "Multimedia (video / audio / imagen)",
    "teacher.add_exercise": "Agregar Ejercicio",
    "teacher.exercise_type": "Tipo de ejercicio",
    "teacher.no_lessons": "Aún no hay lecciones.",
    "teacher.manage": "Gestionar",
    "teacher.review": "Revisar y comentar",
    "teacher.comment": "Comentario del profesor",
    "teacher.save_feedback": "Guardar retroalimentación",
    "teacher.no_submissions": "Aún no hay entregas.",

    "student.my_learning": "Mi Aprendizaje",
    "student.my_learning_sub": "Cursos asignados a ti. Toca un curso para empezar.",
    "student.start": "Empezar a aprender",
    "student.practice": "Practicar",
    "student.no_courses": "Aún no tienes cursos asignados.",
    "student.progress": "Mi Progreso",
    "student.no_progress": "Aún no has practicado ningún ejercicio.",
    "student.your_answer": "Tu respuesta",
    "student.correct_answer": "Respuesta sugerida",
    "student.heard": "Escuchamos",
    "student.feedback": "Retroalimentación",
    "student.score": "Puntaje",
    "student.submit_answer": "Enviar respuesta",
    "student.translate_here": "Escribe tu traducción aquí",
    "student.write_here": "Escribe tu respuesta aquí",
    "student.record_prompt": "Toca grabar, di la frase, luego detén y envía.",
    "student.select_one": "Selecciona la frase que escuchas",
    "student.tabs_lessons": "Lecciones",

    "ex.translation": "Traducción",
    "ex.pronunciation": "Pronunciación",
    "ex.shadowing": "Shadowing",
    "ex.listen_select": "Escuchar y Seleccionar",
    "ex.image_sentence": "Imagen → Oración",
    "ex.reading_repetition": "Lectura y Repetición",
    "ex.guided_writing": "Escritura Guiada",
    "ex.short_quiz": "Prueba Corta",

    "grade.good_job": "Buen trabajo",
    "grade.great_effort": "Gran esfuerzo",
    "grade.getting_better": "Vas mejorando",
    "grade.do_better": "Puedes hacerlo mejor",
    "grade.excellent": "Excelente",
    "grade.try_again": "Intenta nuevamente",

    "ex.sentence_building": "Formar Oración",
    "ex.coming_soon": "Próximamente",

    "student.overview_title": "Tu progreso",
    "student.complete_lesson": "Completar lección",
    "student.completed": "Completada",
    "student.lesson_completed": "¡Lección completada!",
    "student.locked": "Bloqueada",
    "student.locked_hint": "Completa la lección anterior para desbloquear esta.",
    "student.available": "Disponible",
    "student.explanation": "Explicación",
    "student.vocabulary": "Vocabulario",
    "student.examples": "Frases de ejemplo",
    "student.back_to_courses": "Volver a cursos",
    "student.modules": "Módulos",
    "student.percent_complete": "completado",
    "student.studied_today": "¡Estudiaste hoy!",
    "student.keep_going": "¡Sigue así!",
    "student.avg": "Promedio",
    "student.lessons_done": "Lecciones completadas",
    "student.exercises": "ejercicios",
    "student.voice_disabled": "La evaluación de pronunciación por voz estará disponible pronto.",
    "student.tap_words": "Toca las palabras en el orden correcto",
    "student.clear": "Borrar",

    "teacher.add_module": "Agregar Módulo",
    "teacher.new_module": "Nuevo Módulo",
    "teacher.module": "Módulo",
    "teacher.modules": "Módulos",
    "teacher.no_modules": "Aún no hay módulos. Crea uno para organizar tus lecciones.",
    "teacher.select_module": "Selecciona un módulo",
    "teacher.lesson_explanation": "Explicación (gramática / notas)",
    "teacher.status": "Estado",
    "teacher.draft": "Borrador",
    "teacher.published": "Publicado",
    "teacher.move_up": "Subir",
    "teacher.move_down": "Bajar",
    "teacher.no_module": "Sin módulo",
    "teacher.exercises": "ejercicios",
    "common.pdf": "PDF",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem(STORE_KEY) || "es");

  const t = useCallback(
    (key) => (dict[lang] && dict[lang][key]) || (dict.en[key]) || key,
    [lang]
  );

  const toggle = useCallback(() => {
    setLang((prev) => {
      const next = prev === "es" ? "en" : "es";
      localStorage.setItem(STORE_KEY, next);
      return next;
    });
  }, []);

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useI18n = () => useContext(LanguageContext);
